import React, { useState, useEffect } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import type { CollectionNote } from '../../services/schemas/loanServiceSchemas/collectionNote';
import { getCustomerCollectionNotes } from '../../services/collectionNoteService';
import { collectionNoteColumns } from './CollectionNotes';

interface CustomerCollectionNotesProps {
  customerId: string;
  className?: string;
}

const accountColumn: TableColumn<CollectionNote> = {
  name: 'Loan Account',
  selector: (row) => row.accountNumber || '',
  cell: (row) => <span className="fw-semibold small">{row.accountNumber || '-'}</span>,
  sortable: true,
  width: '170px',
};

const columns: TableColumn<CollectionNote>[] = [collectionNoteColumns[0], accountColumn, ...collectionNoteColumns.slice(1)];

/**
 * The customer's collection history: every note on any of their loan accounts (comments, next actions, promises to pay,
 * emails and WhatsApp messages), newest first. Read-only here - notes are added on the loan account itself.
 */
const CustomerCollectionNotes: React.FC<CustomerCollectionNotesProps> = ({ customerId, className = '' }) => {
  const [notes, setNotes] = useState<CollectionNote[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    const controller = new AbortController();
    setIsLoading(true);
    getCustomerCollectionNotes(customerId, { page, size: pageSize }, controller.signal)
      .then((result) => {
        setNotes(result?.content ?? []);
        setTotalRows(result?.totalElements ?? 0);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) console.error('Failed to load collection notes:', error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [customerId, page, pageSize]);

  return (
    <div className={className}>
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-light">
          <h6 className="mb-0">
            <i className="fa fa-history me-2"></i>Collection History
            <span className="badge bg-secondary ms-2">{totalRows}</span>
          </h6>
          <small className="text-muted">Notes on all of this customer's loan accounts: comments, promises to pay, emails and WhatsApp</small>
        </div>
        <div className="card-body p-0">
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <DataTable
              columns={columns}
              data={notes}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={page + 1}
              onChangePage={(newPage) => setPage(newPage - 1)}
              onChangeRowsPerPage={(newPerPage) => { setPageSize(newPerPage); setPage(0); }}
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 20, 50]}
              highlightOnHover
              striped
              responsive
              progressPending={isLoading}
              progressComponent={
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading collection history...</span>
                  </div>
                </div>
              }
              noDataComponent={
                <div className="text-center py-4">
                  <p className="text-muted mb-0">No notes on this customer's loan accounts.</p>
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerCollectionNotes;
